import React, { useState, useEffect } from 'react';
import { getDatabase, ref, onValue, get } from 'firebase/database';
import { getUserProfile, getUserLikedBeans } from '../utils/userProfile';

const UserProfile = ({ user, onClose }) => {
  const [profile, setProfile] = useState(null);
  const [userBeans, setUserBeans] = useState([]);
  const [likedBeans, setLikedBeans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('uploaded');

  const db = getDatabase();

  useEffect(() => {
    const loadUserData = async () => {
      if (!user) return;

      try {
        // Load user profile
        const userProfile = await getUserProfile(user.uid);
        setProfile(userProfile);

        // Load user's uploaded beans
        const beansRef = ref(db, 'beans');
        const unsubscribe = onValue(beansRef, (snapshot) => {
          const data = snapshot.val();
          if (data) {
            const userUploadedBeans = Object.keys(data)
              .filter(beanId => data[beanId].ownerId === user.uid)
              .map(beanId => ({ id: beanId, ...data[beanId] }))
              .sort((a, b) => b.likes - a.likes);
            
            setUserBeans(userUploadedBeans);
          }
        });

        // Load user's liked beans
        const likedBeanIds = await getUserLikedBeans(user.uid);
        const allBeansSnapshot = await get(ref(db, 'beans'));
        const allBeans = allBeansSnapshot.val() || {};
        
        const likedBeansData = likedBeanIds
          .filter(beanId => allBeans[beanId])
          .map(beanId => ({ id: beanId, ...allBeans[beanId] }));
        
        setLikedBeans(likedBeansData);

        return () => unsubscribe();
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [user, db]);

  if (loading) {
    return (
      <div className="profile-container">
        <div className="profile-header">
          <button onClick={onClose} className="back-button">← Back</button>
          <h2>Loading...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-header">
        <button onClick={onClose} className="back-button">← Back</button>
        <h2>Profile</h2>
      </div>
      
      <div className="profile-info">
        <h3>{profile?.displayName || 'Bean Lover'}</h3>
        <div className="profile-stats">
          <div className="stat">
            <span className="stat-number">{userBeans.length}</span>
            <span className="stat-label">Beans Uploaded</span>
          </div>
          <div className="stat">
            <span className="stat-number">
              {userBeans.reduce((sum, bean) => sum + bean.likes, 0)}
            </span>
            <span className="stat-label">Total Likes Received</span>
          </div>
          <div className="stat">
            <span className="stat-number">{likedBeans.length}</span>
            <span className="stat-label">Beans Liked</span>
          </div>
        </div>
      </div>

      <div className="profile-tabs">
        <button 
          className={`tab ${activeTab === 'uploaded' ? 'active' : ''}`}
          onClick={() => setActiveTab('uploaded')}
        >
          My Uploads ({userBeans.length})
        </button>
        <button 
          className={`tab ${activeTab === 'liked' ? 'active' : ''}`}
          onClick={() => setActiveTab('liked')}
        >
          Liked Beans ({likedBeans.length})
        </button>
      </div>

      <div className="bean-grid">
        {activeTab === 'uploaded' && (
          <>
            {userBeans.length === 0 ? (
              <div className="empty-state">
                <p>You haven't uploaded any beans yet!</p>
              </div>
            ) : (
              userBeans.map(bean => (
                <div key={bean.id} className="profile-bean-item">
                  <img src={bean.image} alt={bean.title} />
                  <div className="bean-info">
                    <h4>{bean.title}</h4>
                    <p>❤️ {bean.likes} likes</p>
                    <span className="category">{bean.category}</span>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {activeTab === 'liked' && (
          <>
            {likedBeans.length === 0 ? (
              <div className="empty-state">
                <p>You haven't liked any beans yet!</p>
              </div>
            ) : (
              likedBeans.map(bean => (
                <div key={bean.id} className="profile-bean-item">
                  <img src={bean.image} alt={bean.title} />
                  <div className="bean-info">
                    <h4>{bean.title}</h4>
                    <p>❤️ {bean.likes} likes</p>
                    <span className="category">{bean.category}</span>
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default UserProfile;